import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { ReplaySubject, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { FilterValue } from '../../../../app/interfaces/base/filter-value';
import { FormControl } from '@angular/forms';
import { ApiService } from '../../../../app/services/base/api.service';
import * as _ from 'lodash';
import { IntegratedOptions } from '../../../../app/interfaces/base/integrated-options';
import { isNumeric } from 'rxjs/internal-compatibility';

@Component({
  selector: 'fury-table-filter-select',
  templateUrl: './table-filter-select.component.html',
  styleUrls: ['./table-filter-select.component.scss']
})
export class TableFilterSelectComponent implements AfterViewInit, OnDestroy, OnChanges {

  public isFiltered: boolean = false;

  @Input() options: IntegratedOptions[] = null;
  @Input() name: string = null;
  @Input() multiple: boolean = false;

  @Input() customFiltering: boolean = false;
  @Output() clearFilter: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() filterChange: EventEmitter<string | number | null> = new EventEmitter<string | number | null>();

  public element: FormControl = new FormControl<{ id: string | number | null, type?: string }>(null);
  public filterCtrl: FormControl<string> = new FormControl<string>('');
  public optionElements: ReplaySubject<IntegratedOptions[]> = new ReplaySubject<IntegratedOptions[]>(1);

  private destroy$: Subject<null> = new Subject<null>();

  constructor(private api: ApiService,
              private cd: ChangeDetectorRef) {
  }

  @HostListener('keydown.space', ['$event'])
  @HostListener('click', ['$event']) onClick(e: Event): void {
    e.stopPropagation();
  }

  ngAfterViewInit(): void {
    let timerX: number = 0;
    const timer: NodeJS.Timeout = setInterval((): void => {
      if (this.options && this.options.length) {
        this.optionElements.next(this.options);
        clearInterval(timer);
      }
      if (timerX > 100) {
        clearInterval(timer);
      }
      timerX++;
    }, 100);

    this.element.valueChanges
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe((value: { id: string | number | null, type?: string }): void => {
        this.isFiltered = !!value !== null;
        let filterValue: any = null;

        if (this.multiple) {
          if (_.isArray(value)) {
            const t = [];
            value.forEach((value2: { id: string | number | null, type?: string }): void => {
              if (typeof value2.type !== 'undefined') {
                t.push(value2.id + ',' + value2.type);
              } else {
                t.push(value2.id);
              }
            });
            filterValue = t.join(',');
          } else {
            if (typeof value.type !== 'undefined') {
              filterValue = value.id + ',' + value.type;
            } else {
              filterValue = value.id;
            }
          }
        } else {
          if (typeof value.type !== 'undefined') {
            filterValue = value.id + ',' + value.type;
          } else {
            filterValue = value.id;
          }
        }

        if (this.customFiltering) {
          (filterValue !== null
              ? this.filterChange.emit(filterValue)
              :
              this.clearFilter.next()
          );
        } else {
          (filterValue !== null
              ? this.api.setFilter(this.name, filterValue)
              : this.api.clearFilter(this.name)
          );
        }
      });

    this.filterCtrl.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe((value: string): void => {
      this.optionElements.next(
        value
          ? this.searchOptionGroup(this.options, value)
          : this.options
      );
    });

    this.api.preSetFilters.pipe(
      take(1),
      takeUntil(this.destroy$)
    ).subscribe((filters: FilterValue[]): void => {
      filters.forEach((filter: FilterValue): void => {
        if (this.name === filter.name) {
          this.isFiltered = !!filter.value !== null;
          let filterValue: any = filter.value;

          if (typeof filter.value === 'number' || filter.value === 'none') {
            filterValue = {id: filter.value};
          } else if ((<string>filter.value).indexOf(',') !== -1) {
            const t: string[] | number[] = (<string>filter.value).split(',').map(d => isNumeric(d) ? parseInt(d, 10) : d);

            if (t.every((i: string | number): boolean => parseInt((<string>i), 10) === i)) {
              filterValue = t.map(d => {
                return {id: d};
              });
            } else {
              filterValue = {id: t[0], type: t[1]};
            }
          }

          if (filterValue !== null) {
            if (this.multiple && !_.isArray(filterValue)) {
              filterValue = [filterValue];
            }
            this.element.setValue(filterValue, {emitEvent: false});
            this.element.markAsTouched();
            this.element.updateValueAndValidity();
          }
          setTimeout(() => {
            this.cd.detectChanges();
          }, 100);
        }
      });
    });

    this.api.filterCleared.pipe(takeUntil(this.destroy$)).subscribe((): void => this.clearFilterButton());
    this.api.clearAllFilters.pipe(takeUntil(this.destroy$)).subscribe((): void => this.clearFilterButton(false));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (typeof changes.options.currentValue !== 'undefined') {
      this.optionElements.next(changes.options.currentValue);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  hasOptions(options: IntegratedOptions): boolean {
    return typeof options !== 'undefined' && options !== null && typeof options.options !== 'undefined';
  }

  searchOptionGroup(options: IntegratedOptions[], value: string): IntegratedOptions[] {
    const obj: IntegratedOptions[] = [];
    _.forEach(options, (opt: IntegratedOptions): void => {
      if (typeof opt.options !== 'undefined') {
        const a: IntegratedOptions[] = opt.options.filter((o: IntegratedOptions): boolean => o.name.toLowerCase().indexOf(value.toLowerCase()) > -1);
        if (a.length) {
          obj.push({...opt, ...{options: a}});
        }
      } else {
        if (typeof opt.name !== 'undefined' && opt.name.toLowerCase().indexOf(value.toLowerCase()) > -1) {
          obj.push(opt);
        } else if (typeof opt.display_name !== 'undefined' && opt.display_name.toLowerCase().indexOf(value.toLowerCase()) > -1) {
          obj.push(opt);
        }
      }
    });

    return obj;
  }

  compareWith(i1: { id: string | number }, i2: { id: string | number }): boolean {
    return i1 && i2 && i1.id === i2.id;
  }

  clearFilterButton(emitEvent: boolean = true): void {
    this.element.setValue('', {emitEvent: false});
    this.isFiltered = false;

    if (emitEvent) {
      if (this.customFiltering) {
        this.clearFilter.next();
      } else {
        this.api.clearFilter(this.name);
      }
    }
  }
}
